/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react'
import { Parser } from 'json2csv'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TablePagination from '@material-ui/core/TablePagination'
import TableRow from '@material-ui/core/TableRow'
import CircularProgress from '@material-ui/core/CircularProgress'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import useBuildingsApi from '../../hooks/buildings/useBuildingsApi'
import useApartmentsApi from '../../hooks/apartments/useApartmentsApi'
import useEventsApi from '../../hooks/events/useEventsApi'
import moment from 'moment-timezone'
import Switch from '@material-ui/core/Switch'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { useIsSecurityUser } from '../../hooks/useIsSecurityUser'
import FormControl from '@material-ui/core/FormControl'
import useDebounce from '../../hooks/useDebounce'
import { getTypeLabel } from '../../utils/events'
import useEventsStore from '../../stores/Events'
import { DateTimePicker } from '../common/FormFields'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Button } from '@material-ui/core'
import useAuthStore from '../../stores/Auth'
import { userTypes } from '../../utils/constants'
import { generatePDF } from './EventsReport'
import CustomTable from '../customTable/CustomTable'
import ShowImageTable from '../common/ShowImageTable'
import { format } from 'date-fns'
import ListHeader from '../common/ListHeader'

const eventTypes = [
  'OPEN_DOOR',
  'OPEN_DOOR_PIN',
  'LOGIN_SUCCESS',
  'LOGIN_FAILED',
  'RINGING',
  'OPEN_DOOR_INVITED',
  'ERROR',
  'MANUAL_ENTRY',
  'MANUAL_EXIT',
  'SOS',
]

const FriendlyJSONDisplay = ({ jsonString, isSecurityUser }) => {
  // Parse the JSON string into a JavaScript object
  const jsonObject = JSON.parse(jsonString)

  // Style for the container div
  const containerStyle = {
    borderRadius: '5px',
    padding: '5px',
    maxWidth: '300px',
  }

  // Style for each key-value pair
  const keyValueStyle = {
    marginBottom: '5px',
    fontSize: '14px',
  }

  // Style for keys
  const keyStyle = {
    fontWeight: 'bold',
    marginRight: '5px',
    fontSize: '14px',
    textTransform: 'capitalize',
  }
  const translateKey = (key) => {
    if (key === 'doorBell') {
      return 'Acceso'
    }
    if (key === 'doorBellType') {
      return 'Tipo Portero'
    }
    return key
  }
  const skipKeys = ['Nombre', 'CI']

  return (
    <div style={containerStyle}>
      <ul style={{ paddingLeft: '20px' }}>
        {Object.keys(jsonObject)
          .filter((key) => !(key === 'doorBellType' && isSecurityUser))
          .filter((key) => !skipKeys.includes(key))
          .map(
            (key) =>
              jsonObject[key] && (
                <li key={key} style={keyValueStyle}>
                  <span style={keyStyle}>{translateKey(key)}:</span>{' '}
                  {key === 'Acompañantes'
                    ? displayPayload(jsonObject[key])
                    : JSON.stringify(jsonObject[key]).replace(/"/g, '')}
                </li>
              )
          )
          .filter((item) => !!item)}
      </ul>
    </div>
  )
}

const displayPayload = (payload) => {
  return payload.map(
    (companion) => `${companion.firstName} ${companion.lastName}`
  )
}

export default function EventsList() {
  const eventsStore = useEventsStore((state) => state)

  const getEvents = useDebounce(
    (params) => {
      eventsStore.getEvents(params)
    },
    1000,
    {
      leading: false,
      trailing: true,
    }
  )

  const [showDoorBellEvents, setShowDoorBellEvents] = useState(false)
  const [{ eventsState }] = useEventsApi()
  const loggedUser = useAuthStore((state) => state.user)
  const isGestorOrSecurityAdmin =
    loggedUser?.type === userTypes.gestor ||
    loggedUser?.type === userTypes.securityAdmin

  const classes = useStyles()
  const [page, setPage] = useState(0)
  const [apiPage, setApiPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [{ buildingsState }, { getBuildings }] = useBuildingsApi()
  const [selectedBuilding, setSelectedBuilding] = useState(
    buildingsState?.buildings[0]
  )
  const [selectedApartment, setSelectedApartment] = useState(null)
  const [selectedEventType, setSelectedEventType] = useState(false)
  const isSecurityUser = useIsSecurityUser()

  const getParams = () => {
    let params = {}
    params.offset = apiPage * apiRowsPerPage
    params.limit = apiRowsPerPage
    if (selectedBuilding?.id) params.buildingId = selectedBuilding?.id
    if (selectedApartment?.id) params.apartmentId = selectedApartment?.id
    if (selectedEventType) params.eventType = selectedEventType
    params.dontShowDoorBellEvents = !showDoorBellEvents
    params.search = search
    params.startDate = startDate
    if (endDate) params.endDate = endDate
    return params
  }

  useEffect(() => {
    if (isGestorOrSecurityAdmin && !selectedBuilding) {
      setSelectedBuilding(buildingsState.buildings[0])
    }
  }, [loggedUser?.type, buildingsState.buildings])

  const [search, setSearch] = useState('')
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const prevPageRef = useRef()

  const apiRowsPerPage = 100

  const onSearchChangeDebounced = useDebounce((value) => {
    setPage(0)
    setApiPage(0)
    setSearch(value)
  }, 600)

  const [
    { getApartmentsFromBuildingState },
    { getApartmentsFromBuilding, getApartments },
  ] = useApartmentsApi()

  const columns = [
    {
      id: 'image',
      label: 'Nombre',
      minWidth: 250,
      format: (event) => (
        <ShowImageTable
          imageUrl={event?.user?.selfie}
          name={`${event?.user?.name} ${event?.user?.surname}`}
          ci={event?.user?.ci || event.payload?.CI}
        ></ShowImageTable>
      ),
    },
    {
      id: 'type',
      label: 'Evento',
      minWidth: 170,
      format: (event) => getTypeLabel(event.type, event.payload),
    },
    // {
    //   id: 'user',
    //   label: 'Nombre',
    //   minWidth: 170,
    //   format: (event) =>
    //     event.user
    //       ? event.user?.name
    //         ? `${event?.user?.name} ${event?.user?.surname}`
    //         : event.user?.email
    //       : '-',
    // },
    {
      id: 'user.apartments',
      label: isSecurityUser ? 'Unidad' : 'Cliente/Unidad',
      minWidth: 170,
      format: (event) =>
        event.user && event?.building?.name
          ? `${event?.building?.name} / ${event?.apartment?.name}`
          : '-',
    },
    {
      id: 'payload',
      label: 'Informacion extra',
      minWidth: 100,
      format: (event) => (
        <FriendlyJSONDisplay
          jsonString={JSON.stringify(event.payload)}
          isSecurityUser={isSecurityUser}
        ></FriendlyJSONDisplay>
      ),
    },
    {
      id: 'createdAt',
      label: 'Fecha',
      minWidth: 170,
      format: (event) =>
        moment
          .tz(event.createdAt, 'America/Argentina/Buenos_Aires')
          .format('DD/MM/YYYY HH:mm:ss')
          .toLocaleString(),
    },
  ]

  const downloadFile = (events) => {
    const formattedData = events.data.map((event) => ({
      Evento: event.type,
      Nombre: event.user ? `${event.user.name} ${event.user.surname}` : '-',
      Unidad:
        event.building && event.apartment
          ? `${event.building.name}/${event.apartment.name}`
          : '-',
      'Informacion Extra': event.payload
        ? Object.keys(event.payload)
            .map((key) => `${key}: ${event.payload[key]}`)
            .join('\n')
        : '-',
      Fecha: moment
        .tz(event.createdAt, 'America/Argentina/Buenos_Aires')
        .format('DD/MM/YYYY HH:mm:ss'),
    }))

    const parser = new Parser()
    const csv = parser.parse(formattedData)
    const a = document.createElement('a')
    a.style.display = 'none'
    document.body.appendChild(a)

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    a.href = url
    a.download = 'Eventos.csv'
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }
  if (buildingsState.isLoading) {
    return null
  }

  return (
    <>
      <ListHeader bigTitle="Eventos" canCreate={false} />
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          marginBottom: '20px',
        }}
      >
        <DateTimePicker
          title="Fecha y hora inicio"
          value={startDate}
          onChange={(newValue) => {
            if (endDate) {
              setPage(0)
              setApiPage(0)
            }
            setStartDate(newValue)
          }}
          withRightMargin
        />
        <DateTimePicker
          title="Fecha y hora fin"
          value={endDate}
          onChange={(newValue) => {
            if (startDate) {
              setPage(0)
              setApiPage(0)
            }
            setEndDate(newValue)
          }}
        />
      </div>

      <CustomTable
        loading={eventsStore.loading}
        columns={columns}
        data={eventsStore.events || []}
        getData={(params) => {
          getEvents({
            ...params,
            dontShowDoorBellEvents: true,
            offset: 0,
            limit: apiRowsPerPage,
            endDate,
            startDate,
          })
        }}
        buildingFilter={!selectedBuilding}
        building={selectedBuilding}
        apartmentFilter={true}
        searchFilter={'Acceso, CI, Empresa, etc'}
        options={false}
        serverPagination={false}
        serverCount={eventsStore.events.length}
        customFilters={[
          {
            field: 'type',
            label: 'Tipo de evento',
            type: 'select',
            options: eventTypes.map((type) => ({
              value: type,
              label: getTypeLabel(type, null),
            })),
          },
        ]}
      ></CustomTable>
    </>
  )
}

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  addButton: {
    position: 'absolute',
    right: '32px',
    bottom: '32px',
  },
  checkbox: {
    marginLeft: '8px',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  searchWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '20px',
  },
  switch: {
    marginLeft: '10px',
  },
  '& fieldset': {
    borderColor: '#919191',
  },
  customDateTimePicker: {
    '& .MuiFormLabel-root': {
      fontSize: '14px !important',
      paddingTop: '4px',
      opacity: 0.5,
    },
  },
})
